/**
 * Section-level error boundary for dashboard tabs.
 * Catches runtime errors in a single section and renders an inline
 * ErrorBanner with retry instead of crashing the entire page.
 */

import { Component, type ComponentType, type ErrorInfo, type ReactNode } from 'react';

import { ErrorBanner } from './ErrorBanner';

// ============================================================================
// TYPES
// ============================================================================

interface SectionErrorBoundaryProps {
  children: ReactNode;
  name?: string;
}

interface SectionErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

// ============================================================================
// COMPONENT
// ============================================================================

export class SectionErrorBoundary extends Component<
  SectionErrorBoundaryProps,
  SectionErrorBoundaryState
> {
  constructor(props: SectionErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): SectionErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(`[${this.props.name ?? 'Section'}] Section error:`, error, info);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <ErrorBanner
          message={`Something went wrong in ${this.props.name ?? 'this section'}. Try again or switch to another tab.`}
          onRetry={this.handleRetry}
        />
      );
    }
    return this.props.children;
  }
}

// ============================================================================
// CONVENIENCE WRAPPER
// ============================================================================

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function withSectionBoundary(WrappedComponent: ComponentType<any>, name: string) {
  return function BoundedComponent(props: Record<string, unknown>) {
    return (
      <SectionErrorBoundary name={name}>
        <WrappedComponent {...props} />
      </SectionErrorBoundary>
    );
  };
}
